import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import en from './resources/en';
import es from './resources/es';
import pt from './resources/pt';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'pt'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Key the Settings picker writes to. ONLY written when the user makes an
// explicit choice — never from auto-detection. That means a visitor whose
// browser is Spanish will keep getting Spanish every visit (because the
// detector re-reads navigator each load), and a visitor who chose English
// manually will stay on English even if their browser language changes.
//
// The previous implementation used i18next's `caches: ['localStorage']`
// option which cached the auto-detect result — once set, the first visit
// stuck forever and later browser-language changes were ignored. The
// embed widgets never cached, which is why they translated correctly on
// the same pages where the app didn't.
export const UI_LANG_STORAGE_KEY = 'mixgenie:lng:pick';

export function rememberUiLang(lang: string) {
  try { localStorage.setItem(UI_LANG_STORAGE_KEY, lang); } catch { /* storage denied */ }
}

// One-shot migration from the old auto-caching key (`mixgenie:lng`) to
// the new pick-only key. Users whose previous visit cached 'en' from
// auto-detect — and whose browser language is actually Spanish or
// Portuguese — would otherwise stay stuck on English forever. Clearing
// the old key lets the detector fall through to navigator on this load.
// Safe to run every boot: it only removes the old key if present.
try {
  if (typeof localStorage !== 'undefined' && localStorage.getItem('mixgenie:lng') !== null) {
    localStorage.removeItem('mixgenie:lng');
  }
} catch { /* storage denied */ }

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      pt: { translation: pt },
    },
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // Strip region (es-AR → es) so we match resource bundles.
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      // localStorage only holds a value when the user explicitly picked
      // in Settings; otherwise detection falls through to navigator.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: UI_LANG_STORAGE_KEY,
      // Deliberately NO caches: the detector must not write on its own
      // (see UI_LANG_STORAGE_KEY comment). Settings calls
      // rememberUiLang() directly.
      caches: [],
    },
  });

export default i18n;
